import React, {FC} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

import {PageHero, PageShell, PageTitle, Prose, Section} from 'components';
import {ROUTES} from 'constants/routes';

/**
 * 关于我们（v3，i18n 双语）：/about-us 与 /about-us-cn 两个路由共用本组件，
 * 语言由全局切换器决定（词典 about 命名空间天然双语）。
 * 原样式（AboutUsEn.scss 的 TermsOfUse__* 壳）与 fork 残留的中文版 AboutUs/
 * （thenewboston 区块链内容死代码）均已删除。
 */

/** 产品段落按首个冒号拆为卡标题与描述（中文'：'/英文': '两种分隔） */
const splitProduct = (text: string): {desc: string; title: string} => {
  const [title, ...rest] = text.split(/[：:]/);
  return {desc: rest.join('：').trim(), title: title.trim()};
};

const AboutUsEn: FC = () => {
  const {t} = useTranslation('about');

  /** hero 右侧事实卡：全部取自公司介绍的既有事实，避免编造数字 */
  const facts: {labelKey: string; value: string}[] = [
    {labelKey: 'about.facts.founded', value: '2022'},
    {labelKey: 'about.facts.team', value: t('about.facts.teamValue')},
    {labelKey: 'about.facts.languages', value: '40+'},
    {labelKey: 'about.facts.products', value: '3'},
  ];

  /** 联系信息卡：沿用词典原文（自带前缀） */
  const contactLines: {key: string; text: string}[] = [
    {key: 'companyName', text: t('about.companyName')},
    {key: 'addressSuzhou', text: t('about.addressSuzhou')},
    {key: 'addressShanghai', text: t('about.addressShanghai')},
    {key: 'phone', text: t('about.phone')},
    {key: 'email', text: t('about.email')},
  ];

  const products: {key: string; text: string; to?: string}[] = [
    {key: 'aiImage', text: t('about.productAiImage')},
    {key: 'videoTranslation', text: t('about.productVideoTranslation'), to: ROUTES.videoTranslator},
    {key: 'imageTranslation', text: t('about.productImageTranslation'), to: ROUTES.imageTranslator},
  ];

  return (
    <PageShell>
      <PageTitle canonicalUrl="https://www.xiangjifanyi.com/home/about-us" title={t('about.pageTitle')} />
      <PageHero
        aside={
          <div className="glass-card rounded-3xl p-7 lg:p-8 max-w-xl w-full mx-auto grid grid-cols-2 gap-5">
            {facts.map((fact) => (
              <div key={fact.labelKey}>
                <div className="font-display font-bold text-3xl lg:text-4xl text-indigo-600 tracking-tight">
                  {fact.value}
                </div>
                <div className="text-sm text-slate-500 mt-1.5">{t(fact.labelKey)}</div>
              </div>
            ))}
          </div>
        }
        badge={t('about.heading')}
        description={t('about.heroDesc')}
        title={t('about.heading')}
        variant="light"
      />
      <Section title={t('about.companySectionTitle')}>
        <Prose>
          <p>{t('about.companyIntro')}</p>
        </Prose>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10 max-w-4xl mx-auto">
          {contactLines.slice(0, 3).map((line) => (
            <div className="glass-card rounded-2xl p-6" key={line.key}>
              <p className="text-sm lg:text-base text-slate-600 leading-relaxed">{line.text}</p>
            </div>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-5 mt-5 max-w-4xl mx-auto">
          {contactLines.slice(3).map((line) => (
            <div className="glass-card rounded-2xl p-6" key={line.key}>
              <p className="text-sm lg:text-base text-slate-600 leading-relaxed">{line.text}</p>
            </div>
          ))}
        </div>
      </Section>
      <Section bg="muted" title={t('about.productsHeading')}>
        <div className="grid md:grid-cols-3 gap-6">
          {products.map(({key, text, to}) => {
            const {desc, title} = splitProduct(text);
            return (
              <div className="bg-white rounded-2xl p-7 shadow-sm flex flex-col" key={key}>
                <div className="h-1.5 w-10 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 mb-5" />
                <h3 className="text-lg font-semibold text-slate-900 mb-3">{title}</h3>
                <p className="text-sm lg:text-base text-slate-500 leading-relaxed">{desc}</p>
                {to && (
                  <Link className="text-indigo-600 font-medium mt-5 hover:text-indigo-500 transition-colors" to={to}>
                    {t('about.learnMore')}
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </Section>
    </PageShell>
  );
};

export default AboutUsEn;
